import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {CircularProgress} from "@mui/material";
import {Check, ChevronLeft, FilterList, Payments} from "@mui/icons-material";
import {useNavigate} from "react-router";
import {useSitStores} from "~/stores";
import type {SitConfigurationWriter} from "../../../ApiTypes";
import {SitField, SitSection} from "../../../components/design/SitDesignAtoms";
import i18n from "../../../../i18n/i18n";

const cfg = (k: string) => i18n.t(`config.${k}`, {ns: 'sit', defaultValue: k});

export const SitConfigForm: React.FC = observer(() => {
    const {ccStore} = useSitStores();
    const navigate = useNavigate();
    const [form, setForm] = useState<SitConfigurationWriter>({});
    const [saving, setSaving] = useState(false);
    const config = ccStore.sitConfigRM.result;

    useEffect(() => {
        if (config)
            setForm({
                report_currency_id: config.report_currency?.id ?? null,
                fiscal_reset_month: config.fiscal_reset_month,
                fiscal_year: config.fiscal_year,
                review_interval_weeks: config.review_interval_weeks,
                single_company: config.single_company,
                single_cluster: config.single_cluster,
                single_division: config.single_division,
                single_category: config.single_category,
                values_in_thousands: config.values_in_thousands,
                show_full_category: config.show_full_category,
                show_customer_id: config.show_customer_id,
            });
    }, [config]);

    const numField = (key: keyof SitConfigurationWriter, label: string, units?: string) =>
        <SitField label={label} units={units}>
            <input type="number" value={(form[key] as number) ?? 0}
                   onChange={e => setForm({...form, [key]: parseFloat(e.target.value) || 0})}/>
        </SitField>;

    const checkField = (key: keyof SitConfigurationWriter, label: string) =>
        <label className="sit-formcheck" onClick={() => setForm(f => ({...f, [key]: !f[key]}))}>
            <span className={'sit-formcheck-box' + (form[key] ? ' on' : '')}>
                {form[key] && <Check sx={{fontSize: 12}}/>}
            </span>
            {label}
        </label>;

    const save = () => {
        setSaving(true);
        const done = ccStore.updateSitConfig(form);
        if (done) done.finally(() => setSaving(false)); else setSaving(false);
    };

    return (
        <div className="sit-formscreen">
            <form onSubmit={(event) => { event.preventDefault(); save(); }}>
                <div className="sit-form-head">
                    <button type="button" className="ac-icon-btn" title="Back" onClick={() => navigate(-1)}>
                        <ChevronLeft/>
                    </button>
                    <div className="sit-form-head-mid">
                        <h1 className="sit-settings-title">{cfg('title')}</h1>
                    </div>
                    <button type="button" className="ac-btn" onClick={() => navigate(-1)}>Cancel</button>
                    <button type="submit" className="ac-btn ac-btn--primary" disabled={saving || !config}>
                        {saving ? <CircularProgress size={14}/> : <Check fontSize="small"/>}
                        {cfg('save')}
                    </button>
                </div>

                <div className="sit-form-scroll">
                    <div className="sit-form-col">
                        <SitSection icon={<Payments/>} title={cfg('fiscalSection')} tint="c">
                            <div className="sit-grid">
                                <SitField label={cfg('reportCurrency')}>
                                    <select value={form.report_currency_id ?? ''}
                                            onChange={e => setForm({...form, report_currency_id: e.target.value ? Number(e.target.value) : null})}>
                                        <option value="">—</option>
                                        {ccStore.currencyRM.result?.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                                    </select>
                                </SitField>
                                {numField('fiscal_year', cfg('fiscalYear'))}
                                {numField('fiscal_reset_month', cfg('fiscalResetMonth'), 'month #')}
                                {numField('review_interval_weeks', cfg('reviewIntervalWeeks'), 'weeks')}
                                <div className="sit-scope-checks">
                                    {checkField('values_in_thousands', cfg('valuesInThousands'))}
                                </div>
                            </div>
                        </SitSection>

                        <SitSection icon={<FilterList/>} title={cfg('scopeLimitsSection')} tint="b">
                            <div className="sit-scope-checks">
                                {checkField('single_company', cfg('singleCompany'))}
                                {checkField('single_cluster', cfg('singleCluster'))}
                                {checkField('single_division', cfg('singleDivision'))}
                                {checkField('single_category', cfg('singleCategory'))}
                                {checkField('show_full_category', cfg('showFullCategory'))}
                                {checkField('show_customer_id', cfg('showCustomerId'))}
                            </div>
                        </SitSection>
                    </div>
                </div>
            </form>
        </div>
    );
});
